import * as Sentry from "@sentry/react";
import React, {
  createContext,
  use,
  useState,
  useRef,
  useEffect,
} from "react";
import {
  AddAttachmentMutation,
  AddDocumentMutation,
} from "@lytra/frontend-common/__generated__/graphql";
import { gql } from "@lytra/frontend-common/__generated__/gql";
import { uploadFileToGcs } from "@lytra/frontend-common";
import { useMutation, useApolloClient } from "@apollo/client/react";

// Maximum number of concurrent file uploads to GCS

// Content-hash dedup (JUPITER-441) relies on the caller providing a hash — the
// server-side replace/confirm logic is a no-op without it. Must match the
// sha256 hex format the sync daemon and data-client uploads use.
const hashFileContent = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

// Helper to get folder path from webkitRelativePath (for folder uploads)
const getFolderPath = (file: File): string | undefined => {
  const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  if (relativePath?.includes("/")) {
    const parts = relativePath.split("/");
    parts.pop(); // Remove filename
    return parts.join("/") || undefined;
  }
  return undefined;
};

// ── File-type definitions ─────────────────────────────────────────────────────

// All types accepted by the file picker. Keep in sync with the accept= attribute.
const ALLOWED_FILE_TYPES = [
  // Images
  "image/jpeg",
  "image/png",
  // PDF
  "application/pdf",
  // Word
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  // Excel
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  // PowerPoint
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  // Outlook email messages (browsers often report .msg as octet-stream, so the
  // ".msg" extension below is what actually lets the picker show them)
  "application/vnd.ms-outlook",
  ".msg",
  // Archives
  "application/zip",
  "application/x-zip-compressed",
  "application/gzip",
  "application/x-gzip",
  "application/x-tar",
  "application/x-bzip2",
  "application/x-7z-compressed",
  "application/vnd.rar",
  "application/x-rar-compressed",
  // Plain text / structured text (indexable)
  "text/plain",
  "text/csv",
  "text/markdown",
  "text/html",
  "text/xml",
  "application/json",
  "application/xml",
];

// These types are stored in GCS but not indexed for RAG.
// All others are treated as indexable documents.

// ── GraphQL mutations ─────────────────────────────────────────────────────────

const ADD_DOCUMENT_MUTATION = gql(`
  mutation AddDocument(
    $fileName: String!
    $fileSize: Int64!
    $contentType: String!
    $folder: String
    $confirmReplace: Boolean
    $contentHash: String
  ) {
    documentAdd(
      fileName: $fileName
      fileSize: $fileSize
      contentType: $contentType
      folder: $folder
      confirmReplace: $confirmReplace
      contentHash: $contentHash
    ) {
      id
      presignedUrl
      isResumableUpload
      requiresConfirmation
    }
  }
`);

const ADD_DOCUMENTS_BATCH_MUTATION = gql(`
  mutation AddDocumentsBatch($documents: [DocumentAddInput!]!) {
    documentAddBatch(documents: $documents) {
      documents {
        id
        presignedUrl
        skippedDuplicate
        isMetadataOnly
        fileName
      }
      failedCount
    }
  }
`);

const DOCUMENTS_UPLOAD_COMPLETED_BATCH_MUTATION = gql(`
  mutation SetDocumentsUploadCompletedBatch($documentIds: [UUID!]!, $success: Boolean!) {
    documentUploadCompletedBatch(documentIds: $documentIds, success: $success) {
      id
    }
  }
`);

const ADD_ATTACHMENT_MUTATION = gql(`
  mutation AddAttachment($fileName: String!, $fileSize: Int!, $contentType: String!, $isInline: Boolean!) {
    attachmentAdd(fileName: $fileName, fileSize: $fileSize, contentType: $contentType, isInline: $isInline) {
      id
      presignedUrl
    }
  }
`);

const DOCUMENT_UPLOAD_COMPLETED_MUTATION = gql(`
  mutation SetDocumentUploadCompleted($documentId: UUID!, $success: Boolean!) {
    documentUploadCompleted(documentId: $documentId, success: $success) {
      id
    }
  }
`);

const ATTACHMENT_UPLOAD_COMPLETED_MUTATION = gql(`
  mutation SetAttachmentUploadCompleted($attachmentId: UUID!, $success: Boolean!) {
    attachmentUploadCompleted(attachmentId: $attachmentId, success: $success) {
      id
    }
  }
`);

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FileUpload {
  file: File;
  progress: number;
  status: "queued" | "uploading" | "succeeded" | "failed";
  uploadedBytes: number;
  startTime: number;
  uploadSpeed: number; // Bytes per second
  type: string;
  attachmentId?: string;
  documentId?: string;
  isInline?: boolean;
}

// A prior upload session found in localStorage that the user can resume by
// re-selecting the same file.
export interface PendingResume {
  fileName: string;
  totalSize: number;
  bytesUploaded: number; // As reported by GCS at mount time
  documentId: string;
  resumableUri: string;
  createdAt: number;
}

// A manual document upload the server found would replace an existing file at
// the same path (same name + folder). Surfaced so the UI can ask the user to
// confirm before the server actually overwrites anything.
export interface PendingReplaceConfirmation {
  fileName: string;
}

interface FileUploadContextProps {
  selectedFiles: FileUpload[];
  uploading: boolean;
  timeLeft: number | null;
  pendingResumes: PendingResume[]; // Interrupted uploads from prior sessions
  dismissPendingResumes: () => void;
  handleFileChange: (files: FileList | File[], isInline?: boolean) => Promise<void>;
  handleDocumentUpload: (files: FileList | File[]) => Promise<void>;
  handleSelectAttachments: (isInline?: boolean) => void;
  handleSelectDocuments: () => void;
  handleSelectDocumentsFolder: () => void;
  cancelUpload: (fileName: string) => void;
  cancelAllUploads: () => void;
  getSuccessfulAttachmentIds: () => string[];
  removeFile: (attachmentId: string) => void;
  getUploadingDocuments: () => FileUpload[];
  // Server-confirmed "replace this file?" prompt for the document currently
  // being uploaded, and the answer callback. null when nothing is pending.
  pendingReplaceConfirmation: PendingReplaceConfirmation | null;
  resolvePendingReplaceConfirmation: (replace: boolean) => void;
}

const FileUploadContext = createContext<FileUploadContextProps | undefined>(undefined);

export const useFileUpload = () => {
  const context = use(FileUploadContext);
  if (!context) {
    throw new Error("useFileUpload must be used within a FileUploadProvider");
  }
  return context;
};

// ── Resume-state helpers ──────────────────────────────────────────────────────

interface StoredUpload {
  documentId: string;
  resumableUri: string;
  totalSize: number;
  createdAt: number; // epoch ms
  fileName: string;
}

const STORAGE_KEY_PREFIX = "lytra_upload_";

// Scan all localStorage keys for stored upload sessions (any file, any session).
function loadAllUploadStates(): StoredUpload[] {
  const results: StoredUpload[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(STORAGE_KEY_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (raw) {
        try {
          results.push(JSON.parse(raw) as StoredUpload);
        } catch {
          // corrupt entry — ignore
        }
      }
    }
  } catch {
    // ignore
  }
  return results;
}

function clearUploadStateByKey(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

// Query GCS for bytes already received in a resumable session.
// Returns the number of bytes already uploaded, or null when the session has
// expired or the request fails (caller should start a fresh upload).
//
// NOTE: GCS 308 Resume Incomplete responses omit CORS headers, so browsers block
// them unless redirect:'manual' is used. With redirect:'manual' the response is
// opaque and we cannot read the Range header — so we always return null here.
// This means we can never resume a prior session from the browser; we just start
// a fresh upload. The function still exists to clean up stale localStorage entries.
async function queryResumableProgress(
  resumableUri: string,
  totalSize: number,
): Promise<number | null> {
  try {
    const response = await fetch(resumableUri, {
      method: "PUT",
      headers: {
        "Content-Range": `bytes */${totalSize}`,
        "Content-Length": "0",
      },
      redirect: "manual",
    });

    if (response.ok) {
      return totalSize; // Already complete (200/201 includes CORS headers)
    }
    // opaqueredirect = GCS 308 without CORS headers — can't read Range header
    return null;
  } catch {
    return null;
  }
}

// ── Upload helpers ────────────────────────────────────────────────────────────

// Single-PUT upload for small files (v4 signed URL, 5-min expiry). Shared with the
// assistant-chat attachment uploader, which PUTs to the same kind of presigned URL.
const uploadFile = uploadFileToGcs;


// ── Provider ──────────────────────────────────────────────────────────────────

export const FileUploadProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileUpload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [pendingResumes, setPendingResumes] = useState<PendingResume[]>([]);
  const uploadControllersRef = useRef<Record<string, AbortController>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [selectedIsInline, setSelectedIsInline] = useState<boolean>();
  const [pendingReplaceConfirmation, setPendingReplaceConfirmation] =
    useState<PendingReplaceConfirmation | null>(null);
  const replaceConfirmationResolverRef = useRef<((replace: boolean) => void) | null>(
    null,
  );

  // Suspend the upload of `fileName` until the user answers the replace prompt.
  const waitForReplaceConfirmation = (fileName: string): Promise<boolean> =>
    new Promise((resolve) => {
      replaceConfirmationResolverRef.current = resolve;
      setPendingReplaceConfirmation({ fileName });
    });

  const resolvePendingReplaceConfirmation = (replace: boolean) => {
    replaceConfirmationResolverRef.current?.(replace);
    replaceConfirmationResolverRef.current = null;
    setPendingReplaceConfirmation(null);
  };

  const [uploadDocument] = useMutation(ADD_DOCUMENT_MUTATION);
  const [___uploadDocumentsBatch] = useMutation(ADD_DOCUMENTS_BATCH_MUTATION);
  const [uploadAttachment] = useMutation(ADD_ATTACHMENT_MUTATION);
  const [setDocumentUploadCompleted] = useMutation(DOCUMENT_UPLOAD_COMPLETED_MUTATION, {
    refetchQueries: ["GetFileSystemItems"],
  });
  const [___setDocumentsUploadCompletedBatch] = useMutation(
    DOCUMENTS_UPLOAD_COMPLETED_BATCH_MUTATION,
    { refetchQueries: ["GetFileSystemItems"] },
  );
  const [setAttachmentUploadCompleted] = useMutation(ATTACHMENT_UPLOAD_COMPLETED_MUTATION);
  const apolloClient = useApolloClient();

  // ── On mount: detect interrupted uploads from prior sessions ─────────────
  useEffect(() => {
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const stored = loadAllUploadStates();

    const checkAll = async () => {
      const resumes: PendingResume[] = [];

      for (const entry of stored) {
        // Expire entries older than 7 days (GCS session TTL)
        if (Date.now() - entry.createdAt > sevenDaysMs) {
          // Find and remove the localStorage key
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(STORAGE_KEY_PREFIX)) {
              try {
                const raw = localStorage.getItem(key);
                if (raw) {
                  const parsed = JSON.parse(raw) as StoredUpload;
                  if (parsed.documentId === entry.documentId) {
                    clearUploadStateByKey(key);
                    break;
                  }
                }
              } catch {
                // ignore
              }
            }
          }
          continue;
        }

        const bytesUploaded = await queryResumableProgress(
          entry.resumableUri,
          entry.totalSize,
        );

        if (bytesUploaded === null) {
          // Session expired on GCS — clear stale localStorage entry
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(STORAGE_KEY_PREFIX)) {
              try {
                const raw = localStorage.getItem(key);
                if (raw && (JSON.parse(raw) as StoredUpload).documentId === entry.documentId) {
                  clearUploadStateByKey(key);
                  break;
                }
              } catch {
                // ignore
              }
            }
          }
          continue;
        }

        if (bytesUploaded < entry.totalSize) {
          resumes.push({
            fileName: entry.fileName,
            totalSize: entry.totalSize,
            bytesUploaded,
            documentId: entry.documentId,
            resumableUri: entry.resumableUri,
            createdAt: entry.createdAt,
          });
        }
      }

      if (resumes.length > 0) setPendingResumes(resumes);
    };

    void checkAll();
    // Only run once on mount
     
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (uploading) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [uploading]);

  const updateRemainingTime = () => {
    const totalRemainingTime = selectedFiles.reduce((acc, fileUpload) => {
      if (fileUpload.status === "uploading" && fileUpload.uploadSpeed > 0) {
        const remainingBytes = fileUpload.file.size - fileUpload.uploadedBytes;
        return acc + remainingBytes / fileUpload.uploadSpeed;
      }
      return acc;
    }, 0);
    setTimeLeft(Math.ceil(totalRemainingTime));
  };

  const dismissPendingResumes = () => setPendingResumes([]);

  const handleFileChange = async (files: FileList | File[], isInline?: boolean) => {
    const fileArray = Array.isArray(files) ? files : Array.from(files);
    const newFiles: FileUpload[] = fileArray.map((file) => ({
      file,
      progress: 0,
      status: "queued",
      uploadedBytes: 0,
      startTime: 0,
      uploadSpeed: 0,
      type: file.type,
      isInline,
    }));

    setSelectedFiles((prevFiles) => [...prevFiles, ...newFiles]);
    setUploading(true);

    const startIndex = selectedFiles.length;

    for (const [index, fileUpload] of newFiles.entries()) {
      const globalIndex = startIndex + index;
      // handleFileChange only ever runs from a user-triggered file-input event,
      // never during render, so Date.now() here is safe despite the compiler's
      // (render-purity-focused) static analysis flagging it.
      // eslint-disable-next-line react-hooks/purity
      const startTime = Date.now();

      const controller = new AbortController();
      uploadControllersRef.current[fileUpload.file.name] = controller;

      setSelectedFiles((prev) =>
        prev.map((f, i) =>
          i === globalIndex ? { ...f, status: "uploading", startTime } : f,
        ),
      );

      try {
        const isDocument = fileUpload.isInline === undefined;


        // ── Call documentAdd / attachmentAdd ─────────────────────────────────
        let uploadId: string | undefined;
        let uploadUrl: string | undefined;

        const performDocumentUpload = async (confirmReplace: boolean) => {
          const relativePath = (
            fileUpload.file as File & { webkitRelativePath?: string }
          ).webkitRelativePath;
          const folder = getFolderPath(fileUpload.file);
          const contentType = fileUpload.file.type || "application/octet-stream";
          console.log(
            `[Upload] File: ${fileUpload.file.name}, webkitRelativePath: "${relativePath}", folder: ${folder}, size: ${fileUpload.file.size}, type: ${contentType}`,
          );
          const contentHash = await hashFileContent(fileUpload.file);
          return uploadDocument({
            variables: {
              fileName: fileUpload.file.name,
              fileSize: fileUpload.file.size,
              contentType,
              folder,
              confirmReplace,
              contentHash,
            },
          });
        };

        let { data } =
          fileUpload.isInline !== undefined
            ? await uploadAttachment({
                variables: {
                  fileName: fileUpload.file.name,
                  fileSize: fileUpload.file.size,
                  contentType: fileUpload.file.type,
                  isInline: fileUpload.isInline,
                },
              })
            : await performDocumentUpload(false);

        // Server found an existing file at this path — nothing was created or
        // modified yet. Ask the user, then re-issue the same call with
        // confirmReplace so the actual replace happens only after they agree.
        if (
          fileUpload.isInline === undefined &&
          (data as AddDocumentMutation | undefined)?.documentAdd.requiresConfirmation
        ) {
          const shouldReplace = await waitForReplaceConfirmation(fileUpload.file.name);
          if (!shouldReplace) {
            setSelectedFiles((prev) =>
              prev.map((f, i) => (i === globalIndex ? { ...f, status: "failed" } : f)),
            );
            checkAllUploadsCompleted();
            continue;
          }
          ({ data } = await performDocumentUpload(true));
        }

        if (!data) {
          setSelectedFiles((prev) =>
            prev.map((f, i) => (i === globalIndex ? { ...f, status: "failed" } : f)),
          );
          checkAllUploadsCompleted();
          continue;
        }

        if (isDocument) {
          const addDocumentData = data as AddDocumentMutation;
          uploadId = addDocumentData.documentAdd.id;
          uploadUrl = addDocumentData.documentAdd.presignedUrl ?? undefined;
          console.log(
            `[Upload] Document created: id=${uploadId}, url=${uploadUrl ? uploadUrl.substring(0, 80) + "..." : "NULL"}`,
          );
        } else {
          const addAttachmentData = data as AddAttachmentMutation;
          uploadId = addAttachmentData.attachmentAdd.id;
          uploadUrl = addAttachmentData.attachmentAdd.presignedUrl ?? undefined;
        }

        setSelectedFiles((prev) =>
          prev.map((f, i) =>
            i === globalIndex
              ? { ...f, documentId: isDocument ? uploadId : undefined }
              : f,
          ),
        );

        // No upload URL → duplicate / skipped
        if (!uploadUrl) {
          console.log(
            `[Upload] Skipping upload for ${fileUpload.file.name} - no presigned URL (duplicate or skipped)`,
          );
          setSelectedFiles((prev) =>
            prev.map((f, i) =>
              i === globalIndex
                ? {
                    ...f,
                    progress: 100,
                    status: "succeeded",
                    attachmentId: !isDocument ? uploadId : undefined,
                    documentId: isDocument ? uploadId : undefined,
                  }
                : f,
            ),
          );
          checkAllUploadsCompleted();
          continue;
        }

        // ── Perform the actual upload ─────────────────────────────────────────
        try {
          const onProgress = (uploadedBytes: number, speed: number) => {
            const progress = (uploadedBytes / fileUpload.file.size) * 100;
            setSelectedFiles((prev) =>
              prev.map((f, i) =>
                i === globalIndex
                  ? { ...f, uploadedBytes, progress, uploadSpeed: speed }
                  : f,
              ),
            );
            updateRemainingTime();
          };

          if (uploadUrl) {
            await uploadFile(uploadUrl, fileUpload.file, onProgress, controller.signal);
          }

          if (isDocument) {
            await setDocumentUploadCompleted({
              variables: { documentId: uploadId, success: true },
            });
          } else {
            await setAttachmentUploadCompleted({
              variables: { attachmentId: uploadId, success: true },
            });
          }

          setSelectedFiles((prev) =>
            prev.map((f, i) =>
              i === globalIndex
                ? {
                    ...f,
                    progress: 100,
                    status: "succeeded",
                    attachmentId: !isDocument ? uploadId : undefined,
                    documentId: isDocument ? uploadId : undefined,
                  }
                : f,
            ),
          );
          checkAllUploadsCompleted();
        } catch (error) {
          console.error("Error uploading file:", error);

          const wasAborted =
            error instanceof Error && error.message === "Upload aborted";

          if (!wasAborted) {
            Sentry.captureException(error, {
              extra: {
                fileName: fileUpload.file.name,
                fileSize: fileUpload.file.size,
                contentType: fileUpload.file.type,
                documentId: uploadId,
              },
            });
          }

          if (isDocument) {
            await setDocumentUploadCompleted({
              variables: { documentId: uploadId, success: false },
            });
          } else {
            await setAttachmentUploadCompleted({
              variables: { attachmentId: uploadId, success: false },
            });
          }

          setSelectedFiles((prev) =>
            prev.map((f, i) => (i === globalIndex ? { ...f, status: "failed" } : f)),
          );
          checkAllUploadsCompleted();
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        Sentry.captureException(error, {
          extra: {
            fileName: fileUpload.file.name,
            fileSize: fileUpload.file.size,
            contentType: fileUpload.file.type,
          },
        });
        setSelectedFiles((prev) =>
          prev.map((f, i) => (i === globalIndex ? { ...f, status: "failed" } : f)),
        );
        checkAllUploadsCompleted();
      }
    }

    console.log("[Upload] All uploads processed, refetching documents...");
    await apolloClient.refetchQueries({ include: ["GetFileSystemItems"] });
  };

  const checkAllUploadsCompleted = () => {
    // Read the latest state via the updater callback — `selectedFiles` here is a
    // stale closure over the render that started this upload batch, so checking
    // it directly always evaluates against outdated statuses and never flips
    // `uploading` to false, which in turn stops the "just finished" refetch on
    // the documents page from ever firing.
    setSelectedFiles((prev) => {
      const allCompleted = prev.every(
        (file) => file.status === "succeeded" || file.status === "failed",
      );
      if (allCompleted) setUploading(false);
      return prev;
    });
  };

  const cancelUpload = (fileName: string) => {
    const controller = uploadControllersRef.current[fileName];
    if (controller) {
      controller.abort();
      setSelectedFiles((prev) =>
        prev.map((f) => (f.file.name === fileName ? { ...f, status: "failed" } : f)),
      );
    }
  };

  const cancelAllUploads = () => {
    Object.values(uploadControllersRef.current).forEach((controller) => controller.abort());
    setUploading(false);
    setSelectedFiles((prev) => prev.map((f) => ({ ...f, status: "failed" })));
  };

  const handleSelectAttachments = (isInline?: boolean) => {
    setSelectedIsInline(isInline);
    fileInputRef.current?.click();
  };

  const handleSelectDocuments = () => {
    setSelectedIsInline(undefined);
    fileInputRef.current?.click();
  };

  const handleSelectDocumentsFolder = () => {
    setSelectedIsInline(undefined);
    folderInputRef.current?.click();
  };

  const getSuccessfulAttachmentIds = () =>
    selectedFiles
      .filter((file) => file.status === "succeeded" && file.attachmentId)
      .map((file) => file.attachmentId!)
      .filter(Boolean);

  const removeFile = (attachmentId: string) => {
    setSelectedFiles((prev) => prev.filter((file) => file.attachmentId !== attachmentId));
  };

  const handleDocumentUpload = async (files: FileList | File[]) => {
    await handleFileChange(files);
  };

  const getUploadingDocuments = () =>
    selectedFiles.filter(
      (file) =>
        file.isInline === undefined &&
        (file.status === "queued" || file.status === "uploading"),
    );

  return (
    <FileUploadContext
      value={{
        selectedFiles,
        uploading,
        timeLeft,
        pendingResumes,
        dismissPendingResumes,
        handleFileChange,
        handleDocumentUpload,
        handleSelectAttachments,
        handleSelectDocuments,
        handleSelectDocumentsFolder,
        cancelUpload,
        cancelAllUploads,
        getSuccessfulAttachmentIds,
        removeFile,
        getUploadingDocuments,
        pendingReplaceConfirmation,
        resolvePendingReplaceConfirmation,
      }}
    >
      {children}
      <input
        type="file"
        multiple
        className="hidden"
        ref={fileInputRef}
        onChange={(e) =>
          e.target.files && void handleFileChange(e.target.files, selectedIsInline)
        }
        accept={ALLOWED_FILE_TYPES.join(",")}
      />
      {/* Folder upload — webkitdirectory makes the browser populate webkitRelativePath so folder structure is preserved */}
      <input
        type="file"
        multiple
        className="hidden"
        ref={(el) => {
          folderInputRef.current = el;
          // webkitdirectory/directory aren't in React's InputHTMLAttributes typings
          el?.setAttribute("webkitdirectory", "");
          el?.setAttribute("directory", "");
        }}
        onChange={(e) =>
          e.target.files && void handleFileChange(e.target.files, selectedIsInline)
        }
      />
    </FileUploadContext>
  );
};
